import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Alert, Badge, Spinner, Modal, Table, ProgressBar, Accordion } from 'react-bootstrap';
import { getFirestore, doc, getDoc, collection, getDocs, setDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { getAuth, signOut } from 'firebase/auth';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { FaClock, FaBook, FaUser, FaRupeeSign, FaCalendarAlt, FaGraduationCap, FaCheckCircle, FaSignOutAlt, FaTimesCircle, FaQuestionCircle, FaEdit } from 'react-icons/fa';
import { getDatabase, ref as dbRef, get, set } from 'firebase/database';
import { useNavigate } from 'react-router-dom';

const StudentDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [examStarted, setExamStarted] = useState(false);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [examResult, setExamResult] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [studentNameInput, setStudentNameInput] = useState('');
  const [tempSelectedExam, setTempSelectedExam] = useState(null);

  // Student data states
  const [studentData, setStudentData] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [fees, setFees] = useState(null);
  const [timetable, setTimetable] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [examHistory, setExamHistory] = useState([]);

  const db = getFirestore();
  const auth = getAuth();
  const storage = getStorage();
  const realtimeDb = getDatabase();
  const navigate = useNavigate();

  // Fetch all student data on mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchAllStudentData(user.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  // Add new useEffect to fetch exams when studentData changes
  useEffect(() => {
    if (studentData) {
      fetchExamsFromRealtimeDB();
      fetchExamHistory();
    }
  }, [studentData]);

  // Timer effect
  useEffect(() => {
    let timer;
    if (examStarted && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && examStarted) {
      handleSubmitExam();
    }
    return () => clearInterval(timer);
  }, [examStarted, timeLeft]);

  const fetchAllStudentData = async (userId) => {
    setLoading(true);
    try {
      // Fetch student admission data from Realtime Database
      const admissionsRef = dbRef(realtimeDb, 'admissions');
      const admissionsSnapshot = await get(admissionsRef);
      
      if (admissionsSnapshot.exists()) {
        const admissionsData = admissionsSnapshot.val();
        
        // Find student by userId or use first student as fallback
        let studentInfo = null;
        
        // Method 1: Try to find by userId
        Object.keys(admissionsData).forEach(key => {
          const student = admissionsData[key];
          if (student.userId === userId || student.email === currentUser?.email) {
            studentInfo = { id: key, ...student };
          }
        });
        
        // Method 2: If not found, use first student (for demo)
        if (!studentInfo) {
          const firstKey = Object.keys(admissionsData)[0];
          studentInfo = { id: firstKey, ...admissionsData[firstKey] };
        }
        
        if (studentInfo) {
          setStudentData(studentInfo);
          setStudentNameInput(studentInfo.name || ''); // Pre-fill name if available
          document.title = `${studentInfo.name || 'Student'}'s Dashboard`;

          // Fetch profile image from storage
          if (studentInfo.photoURL) {
            try {
              const imageRef = ref(storage, studentInfo.photoURL);
              const imageUrl = await getDownloadURL(imageRef);
              setProfileImage(imageUrl);
            } catch (imageError) {
              console.log('Using default profile image');
            }
          }
        } else {
          setError('Student data not found in admissions');
        }
      } else {
        setError('No admissions data found');
      }

      // Fetch attendance data from Realtime Database
      try {
        const attendanceRef = dbRef(realtimeDb, 'attendance');
        const attendanceSnapshot = await get(attendanceRef);
        if (attendanceSnapshot.exists()) {
          setAttendance(attendanceSnapshot.val());
        }
      } catch (attendanceError) {
        console.log('Attendance data not available');
      }

      // Fetch fees data from Realtime Database
      try {
        const feesRef = dbRef(realtimeDb, 'fees');
        const feesSnapshot = await get(feesRef);
        if (feesSnapshot.exists()) {
          setFees(feesSnapshot.val());
        }
      } catch (feesError) {
        console.log('Fees data not available');
      }

      // Fetch timetable data from Realtime Database
      try {
        const timetableRef = dbRef(realtimeDb, 'timetable');
        const timetableSnapshot = await get(timetableRef);
        if (timetableSnapshot.exists()) {
          setTimetable(timetableSnapshot.val());
        }
      } catch (timetableError) {
        console.log('Timetable data not available');
      }

    } catch (err) {
      console.error('Error fetching student data:', err);
      setError('Error loading student data: ' + err.message);
    }
    setLoading(false);
  };

  const fetchExamHistory = async () => {
    if (!currentUser?.uid) return;
    
    try {
      const examHistoryRef = dbRef(realtimeDb, `students/${currentUser.uid}/examHistory`);
      const examHistorySnapshot = await get(examHistoryRef);
      
      if (examHistorySnapshot.exists()) {
        const historyData = examHistorySnapshot.val();
        const historyArray = Object.keys(historyData).map(key => ({
          id: key,
          ...historyData[key]
        }));
        setExamHistory(historyArray);
      }
    } catch (err) {
      console.error('Error fetching exam history:', err);
    }
  };

  const fetchExamsFromRealtimeDB = async () => {
    setLoading(true);
    try {
      // Fetch exams data from Realtime Database
      const examsRef = dbRef(realtimeDb, 'exams');
      const examsSnapshot = await get(examsRef);
      
      if (examsSnapshot.exists()) {
        const examsData = examsSnapshot.val();
        const examList = [];
        
        // Convert object to array and filter by student's class and section
        Object.keys(examsData).forEach(examId => {
          const exam = examsData[examId];
          
          // Check if exam matches student's class and section
          if (exam.class === studentData?.class && exam.section === studentData?.section) {
            // Calculate total marks from questions
            let totalMarks = 0;
            if (exam.questions) {
              Object.keys(exam.questions).forEach(questionId => {
                totalMarks += parseInt(exam.questions[questionId].marks) || 0;
              });
            }
            
            examList.push({
              id: examId,
              ...exam,
              totalMarks: totalMarks || exam.totalMarks || 0,
              // Ensure all required fields are present
              examName: exam.examName || 'Unnamed Exam',
              subject: exam.subject || 'General',
              duration: parseInt(exam.duration) || 60,
              status: exam.status || 'Published'
            });
          }
        });
        
        console.log('Fetched exams from Realtime DB:', examList);
        setExams(examList);
      } else {
        console.log('No exams found in Realtime Database');
        setExams([]);
      }
    } catch (err) {
      console.error('Error fetching exams from Realtime DB:', err);
      setError('Error fetching exams: ' + err.message);
      // Fallback to Firestore if Realtime DB fails
      fetchPublishedExamsFromFirestore();
    }
    setLoading(false);
  };

  const fetchPublishedExamsFromFirestore = async () => {
    try {
      // Only fetch exams for the student's class and section
      if (!studentData?.class || !studentData?.section) {
        console.warn('Student class or section not available');
        return;
      }

      const examsRef = collection(db, 'exams');
      const q = query(
        examsRef,
        where('status', '==', 'Published'),
        where('class', '==', studentData.class),
        where('section', '==', studentData.section)
      );
      
      const examsSnapshot = await getDocs(q);
      const examList = [];
      
      examsSnapshot.forEach((doc) => {
        examList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setExams(examList);
    } catch (err) {
      console.error('Error fetching exams from Firestore:', err);
    }
  };

  const handleStartExamClick = (exam) => {
    setTempSelectedExam(exam);
    setShowNameModal(true);
  };

  const confirmStartExam = () => {
    if (!studentNameInput.trim()) {
      setError('Please enter your name to start the exam');
      return;
    }
    
    setShowNameModal(false);
    startExam(tempSelectedExam);
  };

  const startExam = async (exam) => {
    setSelectedExam(exam);
    setTimeLeft(exam.duration * 60);
    
    try {
      // For Realtime Database exams, questions are already in the exam object
      if (exam.questions) {
        // Convert questions object to array
        const questionsArray = Object.keys(exam.questions).map(questionId => ({
          id: questionId,
          ...exam.questions[questionId]
        }));
        
        setSelectedExam({ 
          ...exam, 
          questions: questionsArray 
        });
        setExamStarted(true);
        setCurrentQuestion(0);
        setAnswers({});
      } else {
        // Fallback to Firestore if questions are not in Realtime DB
        const questionsSnapshot = await getDocs(collection(db, 'exams', exam.id, 'questions'));
        const questions = [];
        
        questionsSnapshot.forEach((doc) => {
          const { correctAnswer, ...questionData } = doc.data();
          questions.push({
            id: doc.id,
            ...questionData
          });
        });
        
        setSelectedExam({ ...exam, questions });
        setExamStarted(true);
        setCurrentQuestion(0);
        setAnswers({});
      }
    } catch (err) {
      console.error('Error starting exam:', err);
      setError('Error starting exam: ' + err.message);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmitExam = async () => {
    if (!currentUser || !selectedExam) return;

    setLoading(true);
    try {
      let score = 0;
      let totalMarks = 0;
      let attemptedQuestions = 0;
      let correctQuestions = 0;
      let wrongQuestions = 0;
      const questionResults = [];

      // Calculate score and prepare detailed results
      selectedExam.questions.forEach(question => {
        const marks = parseInt(question.marks) || 1;
        totalMarks += marks;
        const studentAnswer = answers[question.id];
        const isCorrect = studentAnswer === question.correctAnswer;
        const marksObtained = isCorrect ? marks : 0;
        score += marksObtained;

        if (studentAnswer) {
          attemptedQuestions++;
        }

        if (isCorrect) {
          correctQuestions++;
        } else if (studentAnswer && !isCorrect) {
          wrongQuestions++;
        }

        questionResults.push({
          questionId: question.id,
          question: question.question,
          correctAnswer: question.correctAnswer,
          studentAnswer: studentAnswer || 'Not attempted',
          marks: marks,
          marksObtained: marksObtained,
          isCorrect: isCorrect,
          optionA: question.optionA,
          optionB: question.optionB,
          optionC: question.optionC,
          optionD: question.optionD
        });
      });

      const percentage = totalMarks > 0 ? (score / totalMarks) * 100 : 0;
      const submissionTime = new Date();

      // Use the student-entered name or fallback to student data
      const finalStudentName = studentNameInput.trim() || studentData?.name || 'Unknown Student';

      const result = {
        // Student Info
        studentId: currentUser.uid,
        studentName: finalStudentName,
        studentClass: studentData?.class || 'N/A',
        studentSection: studentData?.section || 'N/A',
        studentRollNumber: studentData?.rollNumber || 'N/A',
        studentEmail: studentData?.email || currentUser.email,

        // Exam Info
        examId: selectedExam.id,
        examName: selectedExam.examName,
        subject: selectedExam.subject,
        totalQuestions: selectedExam.questions.length,
        attemptedQuestions,
        correctAnswers: correctQuestions,
        wrongAnswers: wrongQuestions,
        notAttempted: selectedExam.questions.length - attemptedQuestions,

        // Score Info
        score,
        totalMarks,
        percentage,
        status: percentage >= 40 ? 'PASS' : 'FAIL',
        grade: calculateGrade(percentage),

        // Time Info
        examDuration: selectedExam.duration,
        timeSpent: selectedExam.duration * 60 - timeLeft,
        submittedAt: submissionTime.toISOString(),
        examDate: selectedExam.date || submissionTime.toISOString().split('T')[0],

        // Detailed Results
        questionResults,
        
        // Review Status
        reviewStatus: 'pending',
        remarks: '',
        
        // Metadata
        createdAt: submissionTime.toISOString(),
        updatedAt: submissionTime.toISOString()
      };

      // Save to Realtime Database
      const resultId = `${selectedExam.id}_${currentUser.uid}`;
      const resultRef = dbRef(realtimeDb, `examResults/${resultId}`);
      await set(resultRef, result);

      // Update exam completion status
      const examStatusRef = dbRef(realtimeDb, `examStatus/${selectedExam.id}/${currentUser.uid}`);
      await set(examStatusRef, {
        completed: true,
        submittedAt: submissionTime.toISOString(),
        score,
        percentage,
        status: result.status
      });

      // Update student's exam history
      const studentExamHistoryRef = dbRef(realtimeDb, `students/${currentUser.uid}/examHistory/${resultId}`);
      await set(studentExamHistoryRef, {
        examId: selectedExam.id,
        examName: selectedExam.examName,
        subject: selectedExam.subject,
        score,
        totalMarks,
        percentage,
        status: result.status,
        grade: result.grade,
        submittedAt: submissionTime.toISOString(),
        timeSpent: result.timeSpent,
        correctAnswers: correctQuestions,
        wrongAnswers: wrongQuestions,
        notAttempted: result.notAttempted
      });

      console.log('Exam result saved successfully:', result);
      setExamResult(result);
      setExamSubmitted(true);
      setExamStarted(false);
      setError('');
      
      // Refresh exam history
      fetchExamHistory();
    } catch (err) {
      console.error('Error submitting exam:', err);
      setError('Error submitting exam: ' + err.message);
    }
    setLoading(false);
  };

  // Helper function to calculate grade
  const calculateGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Error signing out: ' + error.message);
    }
  };

  const renderStudentProfile = () => (
    <Card className="mb-4 shadow-sm">
      <Card.Body>
        <Row>
          <Col md={3} className="text-center">
            <div className="mb-3">
              {profileImage ? (
                <img 
                  src={profileImage} 
                  alt="Profile" 
                  className="rounded-circle shadow"
                  style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                />
              ) : (
                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center shadow" 
                     style={{ width: '150px', height: '150px', margin: '0 auto' }}>
                  <FaUser size={50} className="text-secondary" />
                </div>
              )}
            </div>
            <Badge bg="primary" className="fs-6">
              Roll No: {studentData?.rollNumber || 'N/A'}
            </Badge>
          </Col>
          <Col md={9}>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h3 className="text-primary">{studentData?.name || 'Loading...'}</h3>
              <Badge bg="success" className="fs-6">Student</Badge>
            </div>
            <Row>
              <Col md={6}>
                <Table borderless size="sm">
                  <tbody>
                    <tr>
                      <td width="140"><strong><FaUser className="me-2" />Father:</strong></td>
                      <td>{studentData?.fatherName || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong><FaGraduationCap className="me-2" />Class:</strong></td>
                      <td>{studentData?.class || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Section:</strong></td>
                      <td>{studentData?.section || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={6}>
                <Table borderless size="sm">
                  <tbody>
                    <tr>
                      <td width="120"><strong>Phone:</strong></td>
                      <td>{studentData?.phone || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Email:</strong></td>
                      <td>{studentData?.email || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Address:</strong></td>
                      <td>{studentData?.address || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const renderDashboardStats = () => (
    <Row className="mb-4">
      <Col md={3}>
        <Card className="h-100 shadow-sm border-0">
          <Card.Body className="text-center">
            <div className="mb-3">
              <FaCalendarAlt size={30} className="text-primary" />
            </div>
            <h6 className="text-muted mb-3">Attendance</h6>
            <h2 className={calculateAttendancePercentage() >= 75 ? "text-success" : "text-warning"}>
              {calculateAttendancePercentage()}%
            </h2>
            <ProgressBar 
              now={calculateAttendancePercentage()} 
              variant={calculateAttendancePercentage() >= 75 ? "success" : "warning"}
              className="mb-2"
              style={{ height: '8px' }}
            />
            <small className="text-muted">
              {attendance?.presentDays || 0} of {attendance?.totalDays || 0} days
            </small>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="h-100 shadow-sm border-0">
          <Card.Body className="text-center">
            <div className="mb-3">
              <FaRupeeSign size={30} className="text-success" />
            </div>
            <h6 className="text-muted mb-3">Fees Status</h6>
            <h2 className={fees?.status === 'Paid' ? "text-success" : "text-danger"}>
              <FaRupeeSign size={20} />
              {fees?.paid || 0}
            </h2>
            <ProgressBar 
              now={fees ? (fees.paid / fees.total) * 100 : 0} 
              variant={fees?.status === 'Paid' ? "success" : "info"}
              className="mb-2"
              style={{ height: '8px' }}
            />
            <small className="text-muted">
              Status: <Badge bg={fees?.status === 'Paid' ? "success" : "warning"}>
                {fees?.status || 'Pending'}
              </Badge>
            </small>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="h-100 shadow-sm border-0">
          <Card.Body className="text-center">
            <div className="mb-3">
              <FaBook size={30} className="text-warning" />
            </div>
            <h6 className="text-muted mb-3">Upcoming Exams</h6>
            <h2 className="text-info">{exams.length}</h2>
            <div className="mt-3">
              {exams.slice(0, 2).map(exam => (
                <Badge key={exam.id} bg="primary" className="me-1 mb-1 fs-6">
                  {exam.subject}
                </Badge>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="h-100 shadow-sm border-0">
          <Card.Body className="text-center">
            <div className="mb-3">
              <FaCheckCircle size={30} className="text-info" />
            </div>
            <h6 className="text-muted mb-3">Exams Completed</h6>
            <h2 className="text-success">{examHistory.length}</h2>
            <div className="mt-3">
              {examHistory.slice(0, 2).map(exam => (
                <Badge key={exam.id} bg={exam.status === 'PASS' ? "success" : "danger"} className="me-1 mb-1 fs-6">
                  {exam.grade}
                </Badge>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderTimetable = () => {
    if (!timetable) return null;

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0"><FaClock className="me-2" />Weekly Timetable</h5>
        </Card.Header>
        <Card.Body>
          <Table responsive bordered hover>
            <thead className="table-dark">
              <tr>
                <th>Time</th>
                <th>Monday</th>
                <th>Tuesday</th>
                <th>Wednesday</th>
                <th>Thursday</th>
                <th>Friday</th>
                <th>Saturday</th>
              </tr>
            </thead>
            <tbody>
              {timetable.schedule && Object.entries(timetable.schedule).map(([time, subjects]) => (
                <tr key={time}>
                  <td><strong>{time}</strong></td>
                  <td>{subjects.Monday || '-'}</td>
                  <td>{subjects.Tuesday || '-'}</td>
                  <td>{subjects.Wednesday || '-'}</td>
                  <td>{subjects.Thursday || '-'}</td>
                  <td>{subjects.Friday || '-'}</td>
                  <td>{subjects.Saturday || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const renderExamHistory = () => {
    if (examHistory.length === 0) return null;

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-success text-white">
          <h5 className="mb-0"><FaCheckCircle className="me-2" />Exam History</h5>
        </Card.Header>
        <Card.Body>
          <Table responsive bordered hover>
            <thead className="table-dark">
              <tr>
                <th>Exam Name</th>
                <th>Subject</th>
                <th>Score</th>
                <th>Percentage</th>
                <th>Grade</th>
                <th>Status</th>
                <th>Correct</th>
                <th>Wrong</th>
                <th>Not Attempted</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {examHistory.map((exam) => (
                <tr key={exam.id}>
                  <td><strong>{exam.examName}</strong></td>
                  <td>{exam.subject}</td>
                  <td>{exam.score}/{exam.totalMarks}</td>
                  <td>{exam.percentage?.toFixed(1)}%</td>
                  <td>
                    <Badge bg={
                      exam.grade === 'A+' ? 'success' : 
                      exam.grade === 'A' ? 'primary' :
                      exam.grade === 'B+' ? 'info' :
                      exam.grade === 'B' ? 'warning' :
                      exam.grade === 'C' ? 'secondary' :
                      exam.grade === 'D' ? 'light' : 'danger'
                    }>
                      {exam.grade}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={exam.status === 'PASS' ? 'success' : 'danger'}>
                      {exam.status}
                    </Badge>
                  </td>
                  <td className="text-success">
                    <strong>{exam.correctAnswers || 0}</strong>
                  </td>
                  <td className="text-danger">
                    <strong>{exam.wrongAnswers || 0}</strong>
                  </td>
                  <td className="text-warning">
                    <strong>{exam.notAttempted || 0}</strong>
                  </td>
                  <td>{new Date(exam.submittedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const renderExamQuestions = () => (
    <div className="exam-container">
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h3 className="text-primary">{selectedExam.examName}</h3>
              <p className="mb-0 text-muted fs-5">
                {selectedExam.subject} | Total Marks: {selectedExam.totalMarks}
              </p>
              <p className="mb-0 text-muted">
                Student: <strong>{studentNameInput || studentData?.name}</strong> | Class: {studentData?.class}-{studentData?.section} | Roll No: {studentData?.rollNumber}
              </p>
            </div>
            <div className="text-center">
              <h5 className="mb-2">Time Left</h5>
              <Badge bg="warning" className="px-3 py-2 fs-6">
                {formatTime(timeLeft)}
              </Badge>
            </div>
          </div>
          <ProgressBar 
            now={(currentQuestion + 1) / selectedExam.questions.length * 100} 
            variant="info"
            className="mb-3"
            style={{ height: '10px' }}
          />
          <div className="text-center">
            <Badge bg="secondary" className="fs-6">
              Question {currentQuestion + 1} of {selectedExam.questions.length}
            </Badge>
          </div>
        </Card.Body>
      </Card>

      {selectedExam.questions?.map((question, index) => (
        <Card 
          key={question.id} 
          className={`mb-3 shadow-sm ${index === currentQuestion ? '' : 'd-none'}`}
        >
          <Card.Body>
            <div className="d-flex justify-content-between mb-3">
              <h5 className="text-primary">Question {index + 1}</h5>
              <Badge bg="primary" className="fs-6">{question.marks} marks</Badge>
            </div>
            <p className="question-text fs-5">{question.question}</p>
            <Form>
              {['A', 'B', 'C', 'D'].map((option) => (
                <Form.Check
                  key={option}
                  type="radio"
                  id={`${question.id}-${option}`}
                  className="option-radio mb-3"
                  label={
                    <div className="option-text p-3 rounded">
                      <strong>{option}.</strong> {question[`option${option}`]}
                    </div>
                  }
                  name={question.id}
                  onChange={() => handleAnswer(question.id, option)}
                  checked={answers[question.id] === option}
                />
              ))}
            </Form>
          </Card.Body>
          <Card.Footer>
            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="outline-primary"
                disabled={currentQuestion === 0}
                onClick={() => setCurrentQuestion(prev => prev - 1)}
                className="px-4"
              >
                Previous
              </Button>
              
              <div className="text-center">
                <Badge bg="light" text="dark" className="fs-6">
                  {currentQuestion + 1} / {selectedExam.questions.length}
                </Badge>
              </div>

              {currentQuestion < selectedExam.questions.length - 1 ? (
                <Button
                  variant="primary"
                  onClick={() => setCurrentQuestion(prev => prev + 1)}
                  className="px-4"
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="success"
                  onClick={handleSubmitExam}
                  disabled={loading}
                  className="px-4"
                >
                  {loading ? <Spinner size="sm" /> : 'Submit Exam'}
                </Button>
              )}
            </div>
          </Card.Footer>
        </Card>
      ))}
    </div>
  );

  const renderQuestionReview = () => {
    if (!examResult) return null;

    return (
      <div className="question-review">
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-info text-white">
            <h4 className="mb-0">
              <FaQuestionCircle className="me-2" />
              Question-wise Review - {examResult.examName}
            </h4>
          </Card.Header>
          <Card.Body>
            <Accordion>
              {examResult.questionResults.map((question, index) => (
                <Accordion.Item key={question.questionId} eventKey={index.toString()}>
                  <Accordion.Header>
                    <div className="d-flex align-items-center w-100">
                      <span className="me-3">Question {index + 1}</span>
                      <div className="ms-auto">
                        {question.isCorrect ? (
                          <Badge bg="success" className="me-2">
                            <FaCheckCircle className="me-1" />
                            Correct (+{question.marksObtained})
                          </Badge>
                        ) : question.studentAnswer === 'Not attempted' ? (
                          <Badge bg="warning" className="me-2">
                            Not Attempted
                          </Badge>
                        ) : (
                          <Badge bg="danger" className="me-2">
                            <FaTimesCircle className="me-1" />
                            Wrong (0/{question.marks})
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="mb-3">
                      <h6>Question:</h6>
                      <p className="question-text p-3 bg-light rounded">{question.question}</p>
                    </div>
                    
                    <Row>
                      <Col md={6}>
                        <h6>Options:</h6>
                        <ul className="list-unstyled">
                          <li className={`p-2 mb-1 rounded ${question.correctAnswer === 'A' ? 'bg-success text-white' : 'bg-light'}`}>
                            <strong>A:</strong> {question.optionA}
                            {question.correctAnswer === 'A' && <FaCheckCircle className="ms-2" />}
                          </li>
                          <li className={`p-2 mb-1 rounded ${question.correctAnswer === 'B' ? 'bg-success text-white' : 'bg-light'}`}>
                            <strong>B:</strong> {question.optionB}
                            {question.correctAnswer === 'B' && <FaCheckCircle className="ms-2" />}
                          </li>
                          <li className={`p-2 mb-1 rounded ${question.correctAnswer === 'C' ? 'bg-success text-white' : 'bg-light'}`}>
                            <strong>C:</strong> {question.optionC}
                            {question.correctAnswer === 'C' && <FaCheckCircle className="ms-2" />}
                          </li>
                          <li className={`p-2 mb-1 rounded ${question.correctAnswer === 'D' ? 'bg-success text-white' : 'bg-light'}`}>
                            <strong>D:</strong> {question.optionD}
                            {question.correctAnswer === 'D' && <FaCheckCircle className="ms-2" />}
                          </li>
                        </ul>
                      </Col>
                      <Col md={6}>
                        <h6>Your Answer:</h6>
                        <div className={`p-3 rounded ${question.isCorrect ? 'bg-success text-white' : question.studentAnswer === 'Not attempted' ? 'bg-warning' : 'bg-danger text-white'}`}>
                          <strong>
                            {question.studentAnswer === 'Not attempted' 
                              ? 'Not Attempted' 
                              : `Option ${question.studentAnswer}: ${question[`option${question.studentAnswer}`]}`}
                          </strong>
                          {!question.isCorrect && question.studentAnswer !== 'Not attempted' && (
                            <div className="mt-2">
                              <small>
                                <FaTimesCircle className="me-1" />
                                Incorrect Answer
                              </small>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-3 p-3 bg-primary text-white rounded">
                          <h6>Correct Answer:</h6>
                          <strong>Option {question.correctAnswer}: {question[`option${question.correctAnswer}`]}</strong>
                        </div>
                        
                        <div className="mt-3">
                          <Badge bg="secondary" className="fs-6">
                            Marks: {question.marksObtained}/{question.marks}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderNameModal = () => (
    <Modal show={showNameModal} onHide={() => setShowNameModal(false)} centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaEdit className="me-2" />
          Enter Your Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <strong>Important:</strong> Please enter your full name as it should appear on the exam result.
        </Alert>
        
        <Form.Group className="mb-3">
          <Form.Label>
            <strong>Your Full Name</strong>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter your full name"
            value={studentNameInput}
            onChange={(e) => setStudentNameInput(e.target.value)}
            required
            size="lg"
          />
          <Form.Text className="text-muted">
            This name will be used for your exam result and certificate.
          </Form.Text>
        </Form.Group>

        {tempSelectedExam && (
          <Card className="bg-light">
            <Card.Body>
              <h6>Exam Details:</h6>
              <p className="mb-1"><strong>{tempSelectedExam.examName}</strong></p>
              <p className="mb-1">Subject: {tempSelectedExam.subject}</p>
              <p className="mb-1">Duration: {tempSelectedExam.duration} minutes</p>
              <p className="mb-0">Total Marks: {tempSelectedExam.totalMarks}</p>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowNameModal(false)}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={confirmStartExam}
          disabled={!studentNameInput.trim()}
        >
          <FaCheckCircle className="me-2" />
          Start Exam
        </Button>
      </Modal.Footer>
    </Modal>
  );

  if (loading && !studentData) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const renderResultModal = () => (
    <Modal show={examSubmitted} onHide={() => setExamSubmitted(false)} centered size="lg">
      <Modal.Header closeButton className={`${examResult?.status === 'PASS' ? 'bg-success' : 'bg-danger'} text-white`}>
        <Modal.Title>
          <FaCheckCircle className="me-2" />
          Exam Result - {examResult?.studentName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {examResult && (
          <>
            <div className="text-center mb-4">
              <h3>{examResult.examName}</h3>
              <p className="text-muted">{examResult.subject}</p>
              <div className="mb-3">
                <Badge bg="primary" className="me-2 fs-6">
                  Class: {examResult.studentClass}-{examResult.studentSection}
                </Badge>
                <Badge bg="secondary" className="me-2 fs-6">
                  Roll No: {examResult.studentRollNumber}
                </Badge>
              </div>
              <Badge bg={examResult.status === 'PASS' ? 'success' : 'danger'} className="fs-5 mb-3">
                {examResult.status}
              </Badge>
            </div>

            <Row className="mb-4">
              <Col md={3} className="text-center mb-3">
                <h2 className={examResult.percentage >= 40 ? "text-success" : "text-danger"}>
                  {examResult.percentage.toFixed(1)}%
                </h2>
                <small className="text-muted">Percentage</small>
              </Col>
              <Col md={3} className="text-center mb-3">
                <h2 className="text-primary">{examResult.score}/{examResult.totalMarks}</h2>
                <small className="text-muted">Score</small>
              </Col>
              <Col md={3} className="text-center mb-3">
                <h2 className="text-info">{examResult.grade}</h2>
                <small className="text-muted">Grade</small>
              </Col>
              <Col md={3} className="text-center mb-3">
                <h2>{Math.floor(examResult.timeSpent / 60)}:{(examResult.timeSpent % 60).toString().padStart(2, '0')}</h2>
                <small className="text-muted">Time Taken</small>
              </Col>
            </Row>

            <Card className="mb-4">
              <Card.Header className="bg-light">
                <h5 className="mb-0">Detailed Analysis</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3} className="text-center mb-3">
                    <h4 className="text-success">{examResult.correctAnswers}</h4>
                    <small className="text-muted">Correct Answers</small>
                  </Col>
                  <Col md={3} className="text-center mb-3">
                    <h4 className="text-danger">{examResult.wrongAnswers}</h4>
                    <small className="text-muted">Wrong Answers</small>
                  </Col>
                  <Col md={3} className="text-center mb-3">
                    <h4 className="text-warning">{examResult.notAttempted}</h4>
                    <small className="text-muted">Not Attempted</small>
                  </Col>
                  <Col md={3} className="text-center mb-3">
                    <h4 className="text-info">{examResult.totalQuestions}</h4>
                    <small className="text-muted">Total Questions</small>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="text-center mb-3">
              <Button 
                variant="outline-primary" 
                onClick={() => setShowReview(true)}
                className="me-2"
              >
                <FaQuestionCircle className="me-1" />
                Review Questions
              </Button>
            </div>

            <Alert variant="info">
              Your result has been submitted and will be available in your exam history.
              You can review each question to see your answers and the correct solutions.
            </Alert>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-center">
        <Button variant="primary" onClick={() => {
          setExamSubmitted(false);
          setSelectedExam(null);
          setAnswers({});
          setCurrentQuestion(0);
        }} className="px-5">
          Back to Dashboard
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate attendance percentage
  const calculateAttendancePercentage = () => {
    if (!attendance || !attendance.days) return 0;
    
    const totalDays = attendance.totalDays || Object.keys(attendance.days).length;
    const presentDays = attendance.presentDays || Object.values(attendance.days).filter(day => day.status === 'present').length;
    
    return totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : 0;
  };

  return (
    <div className="p-4 bg-light min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="text-primary mb-1">Student Dashboard</h1>
          <p className="text-muted">Welcome to your learning portal</p>
        </div>
        <Button variant="outline-danger" onClick={handleLogout} className="d-flex align-items-center">
          <FaSignOutAlt className="me-2" />
          Logout
        </Button>
      </div>

      {error && (
        <Alert variant={error.includes('successfully') ? "success" : "danger"} dismissible onClose={() => setError('')} className="mb-4">
          {error}
        </Alert>
      )}

      {!examStarted && !showReview ? (
        <>
          {renderStudentProfile()}
          {renderDashboardStats()}
          {renderTimetable()}
          {renderExamHistory()}
          
          <Card className="shadow-sm">
            <Card.Header className="bg-info text-white">
              <h4 className="mb-0"><FaBook className="me-2" />Available Exams</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                {exams.length > 0 ? (
                  exams.map(exam => (
                    <Col md={6} lg={4} key={exam.id} className="mb-3">
                      <Card className="h-100 shadow-sm border-0">
                        <Card.Body className="d-flex flex-column">
                          <h5 className="text-primary">{exam.examName}</h5>
                          <p className="text-muted flex-grow-1">
                            <FaBook className="me-2" />
                            {exam.subject}
                          </p>
                          <div className="mb-2">
                            <small className="text-muted">
                              <FaClock className="me-1" />
                              Duration: {exam.duration} minutes
                            </small>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">
                              Date: {exam.date || 'Not specified'}
                            </small>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-auto">
                            <Badge bg="secondary">Marks: {exam.totalMarks}</Badge>
                            <Button 
                              variant="primary"
                              onClick={() => handleStartExamClick(exam)}
                              disabled={loading}
                              size="sm"
                            >
                              Start Exam
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))
                ) : (
                  <Col>
                    <div className="text-center py-4">
                      <FaBook size={50} className="text-muted mb-3" />
                      <p className="text-muted fs-5">No exams available at the moment.</p>
                    </div>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        </>
      ) : showReview ? (
        <>
          {renderQuestionReview()}
          <div className="text-center">
            <Button variant="primary" onClick={() => setShowReview(false)}>
              Back to Dashboard
            </Button>
          </div>
        </>
      ) : (
        renderExamQuestions()
      )}

      {renderNameModal()}
      {renderResultModal()}

      <style>{`
        .option-text {
          background-color: #f8f9fa;
          border: 2px solid #dee2e6;
          border-radius: 8px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .form-check-input:checked + .option-text,
        .option-text:hover {
          background-color: #e3f2fd;
          border-color: #0d6efd;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .question-text {
          font-size: 1.1rem;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #0d6efd;
        }
        .exam-container {
          max-width: 900px;
          margin: 0 auto;
        }
        .card {
          border: none;
          border-radius: 12px;
        }
        .btn {
          border-radius: 8px;
          font-weight: 500;
        }
        .accordion-button {
          font-weight: 500;
        }
        .question-review .accordion-body {
          background-color: #f8f9fa;
        }
      `}</style>
    </div>
  );
};

export default StudentDashboard;